import type { components } from '@pba/types-openapi-client';
import {
  addDays,
  addHours,
  addMinutes,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  isWithinInterval,
  max,
  parseISO,
  setHours,
  setMinutes,
  startOfMinute,
} from 'date-fns';

import { ChangeRequest, getLastChangeRequestStatus } from './change-request';
import { Entity, type EntityReference, createEntityReference } from './entity';
import type { EventParticipantFeedback, EventRecordingStatus, EventStatus, EventType } from './event';
import { type Image, createImage } from './image';
import type { EventPrivacy } from './privacy';
import type {
  ChangeRequestStatus,
  ProgramAbstractEventDataObject,
  ProgramEnterEventDataObject,
  ProgramEventDataObject,
  ProgramEventMetaDataObject,
  ProgramEventMetaItemDataObject,
  ProgramEventSlimDataObject,
  ProgramListEventDataObject,
  ProgramPublicEventDataObject,
} from './service';
import type { Tag } from './tag';
import { type UserList, createUserList } from './user';

export enum ProgramInviteType {
  empty = '',
  invitees = 'invitees',
  moderators = 'moderators',
  presenters = 'presenters',
}

export type ProgramDialInInformation = {
  id: string;
  number: string;
  passcode: string;
};

export type ProgramEmailEventInvitee = {
  email: string;
  type: 'email_invitee';
} & ProgramEventInviteeBase;

export type ProgramEnterEvent = {
  enterUrl: string;
};

export type ProgramEventFile = {
  bigMarkerId: string;
  byteSize: number;
  checksum: string;
  contentType: string;
  createdAt: string;
  downloadUrl: string;
  filename: string;
  id: string;
  url: string;
};

export type ProgramEventInvitee = ProgramEmailEventInvitee | ProgramRegisteredEventInvitee;

export type ProgramEventInviteeRole = 'attendee' | 'moderator' | 'presenter';

export type ProgramEventListType = EventType | ProgramEventType;
export type ProgramEventStatus = 'draft' | 'published';

export type ProgramEventType = 'conference' | 'webinar';

export type ProgramNonPendingUserRsvp = Exclude<ProgramUserRsvp, 'pending'>;

export type ProgramRegisteredEventInvitee = {
  type: 'invitee';
} & ProgramEventInviteeBase &
  UserList;

export type ProgramUserRsvp = 'going' | 'not_going' | 'pending';

type ProgramEventInviteeBase = {
  eventRole: ProgramEventInviteeRole;
  isAuthor: boolean;
  rsvp: ProgramUserRsvp;
};

export const createProgramEnterEvent = (data: ProgramEnterEventDataObject): ProgramEnterEvent => ({
  enterUrl: data.enter_uri,
});

export const isRegisteredProgramInvitee = (value?: ProgramEventInvitee): value is ProgramRegisteredEventInvitee =>
  value?.type === 'invitee';

export const createProgramRegisteredEventInvitee = (
  data: components['schemas']['EventUserInvitee'],
): ProgramRegisteredEventInvitee => ({
  ...createUserList(data),
  eventRole: data.event_role,
  isAuthor: data.is_author,
  rsvp: data.rsvp,
  type: data.type,
});

export const createProgramEmailEventInvitee = (
  data: components['schemas']['EventUserEmailInvitee'],
): ProgramEmailEventInvitee => ({
  email: data.email,
  eventRole: data.event_role,
  isAuthor: data.is_author,
  rsvp: data.rsvp,
  type: data.type,
});

export const createProgramEventInvitee = (data: components['schemas']['EventUserList']): ProgramEventInvitee => {
  if (data.type === 'email_invitee') {
    return createProgramEmailEventInvitee(data);
  }

  return createProgramRegisteredEventInvitee(data);
};

export type ProgramConferenceOrWebinarFeedback = {
  audioText?: string;
  helpfulText?: string;
  isAudioOk: number;
  isHelpful: number;
  isVideoOk: number;
  videoText?: string;
};

export type ProgramEventFeedback = EventParticipantFeedback | ProgramConferenceOrWebinarFeedback;

export type ProgramEventSlim = {
  coverPhoto?: Image;
  endTime: Date;
  eventType: ProgramEventType;
  goingInviteesCount: number;
  nodeId?: string;
  place?: string;
  startTime: Date;
} & Entity;

type ProgramEventMetaItem = {
  name: string;
  translations: {
    en: string;
    es: string;
  };
};

const createProgramEventMetaItem = (data: ProgramEventMetaItemDataObject): ProgramEventMetaItem => ({
  name: data.name,
  translations: data.translations,
});

type ProgramEventMeta = {
  patientType?: ProgramEventMetaItem;
  serviceType?: ProgramEventMetaItem[];
};

const createProgramEventMeta = (data: ProgramEventMetaDataObject): ProgramEventMeta => ({
  patientType: data.patient_type ? createProgramEventMetaItem(data.patient_type) : undefined,
  serviceType: data.service_type?.map((serviceType) => createProgramEventMetaItem(serviceType)),
});

export type ProgramEvent = {
  administrators: UserList[];
  bmgId: null | string;
  changeRequests?: ChangeRequest[];
  coverPhoto?: Image;
  currentUserRole: ProgramEventInviteeRole;
  currentUserRsvp: ProgramUserRsvp;
  dialInInformation?: ProgramDialInInformation;
  duration: number;
  endTime: Date;
  eventsFiles: ProgramEventFile[];
  eventType: ProgramEventType;
  featuredInChannels?: boolean;
  feedbacked: boolean;
  goingInviteesCount: number;
  htmlContent: null | string;
  id: string;
  invitees: UserList[];
  inviteesCount: number;
  meta?: ProgramEventMeta;
  name: string;
  notGoingInviteesCount: number;
  pendingInviteesCount: number;
  recordingStatus: EventRecordingStatus;
  recordingUrl?: string;
  showInChannels?: boolean;
  startTime: Date;
  status: EventStatus | ProgramEventStatus;
  tags?: Tag[];
  textContent: string;
  viewPermission: EventPrivacy;
};

export abstract class ProgramAbstractEvent extends Entity {
  static readonly enterOffset: Record<ProgramEventInviteeRole, number> = {
    attendee: -15,
    moderator: -60,
    presenter: -60,
  };

  static get leaveOffset() {
    return 58;
  }

  bmgId: null | string;

  coverPhoto?: Image;
  endTime: Date;
  recordingStatus: EventRecordingStatus;
  recordingUrl?: string;
  startTime: Date;
  status: EventStatus | ProgramEventStatus;

  get isAfterOpenToJoin() {
    const endTime = addMinutes(this.endTime, ProgramAbstractEvent.leaveOffset);
    return isAfter(new Date(), endTime);
  }

  get isLive() {
    return this.isPublished && isProgramEventLive({ endTime: this.endTime, startTime: this.startTime });
  }

  get isPublished() {
    return this.status === 'published';
  }

  constructor(data: ProgramAbstractEventDataObject) {
    super(data);

    this.coverPhoto = data.cover_photo ? createImage(data.cover_photo) : undefined;
    this.bmgId = data.bmg_id;
    this.endTime = new Date(data.end_time);
    this.startTime = new Date(data.start_time);
    this.recordingStatus = data.recording_status;
    this.recordingUrl = data.recording_url ?? undefined;
    this.status = data.status;
  }

  static getEnterOffset(role: ProgramEventInviteeRole) {
    return ProgramAbstractEvent.enterOffset[role];
  }

  isBeforeOpenToJoin(role: ProgramEventInviteeRole) {
    const startTime = addMinutes(this.startTime, ProgramAbstractEvent.getEnterOffset(role));

    return isBefore(new Date(), startTime);
  }
}

export const createProgramEvent = (data: ProgramEventDataObject): ProgramEvent => ({
  administrators: data.administrators.map((administrator) => createUserList(administrator)),
  bmgId: data.bmg_id,
  changeRequests: data.change_requests?.map((changeRequest) => new ChangeRequest(changeRequest)),
  coverPhoto: data.cover_photo ? createImage(data.cover_photo) : undefined,
  currentUserRole: data.current_user_role,
  currentUserRsvp: data.current_user_rsvp,
  dialInInformation: data.dial_in_information,
  duration: data.duration,
  endTime: new Date(data.end_time),
  eventsFiles: data.events_files.map((file) => ({
    bigMarkerId: file.big_marker_id,
    byteSize: file.byte_size,
    checksum: file.checksum,
    contentType: file.content_type,
    createdAt: file.created_at,
    downloadUrl: file.download_url,
    filename: file.filename,
    id: file.id,
    url: file.url,
  })),
  eventType: data.event_type,
  featuredInChannels: data.featured_in_channels,
  feedbacked: data.feedbacked,
  goingInviteesCount: data.going_invitees_count,
  htmlContent: data.html_content,
  id: data.id,
  invitees: data.invitees.map((invitee) => createUserList(invitee)),
  inviteesCount: data.invitees_count,
  meta: data.meta ? createProgramEventMeta(data.meta) : undefined,
  name: data.name,
  notGoingInviteesCount: data.not_going_invitees_count,
  pendingInviteesCount: data.pending_invitees_count,
  recordingStatus: data.recording_status,
  recordingUrl: data.recording_url ?? undefined,
  showInChannels: data.show_in_channels,
  startTime: new Date(data.start_time),
  status: data.status,
  tags: data.tags,
  textContent: data.text_content,
  viewPermission: data.view_permission,
});

export type ProgramListEvent = {
  author: EntityReference;
  bmgId: null | string;
  changeRequests?: ChangeRequest[];
  coverPhoto?: Image;
  currentUserRole: ProgramEventInviteeRole;
  currentUserRsvp: ProgramUserRsvp;
  dialInInformation?: ProgramDialInInformation;
  endTime: Date;
  eventType: ProgramEventType;
  goingInviteesCount: number;
  id: string;
  inviteesCount: number;
  name: string;
  recordingStatus: EventRecordingStatus;
  recordingUrl?: string;
  startTime: Date;
  status: EventStatus | ProgramEventStatus;
};

export const createProgramListEvent = (data: ProgramListEventDataObject): ProgramListEvent => ({
  author: createEntityReference(data.author),
  bmgId: data.bmg_id,
  changeRequests: data.change_requests?.map((changeRequest) => new ChangeRequest(changeRequest)),
  coverPhoto: data.cover_photo ? createImage(data.cover_photo) : undefined,
  currentUserRole: data.current_user_role,
  currentUserRsvp: data.current_user_rsvp,
  dialInInformation: data.dial_in_information,
  endTime: new Date(data.end_time),
  eventType: data.event_type,
  goingInviteesCount: data.going_invitees_count,
  id: data.id,
  inviteesCount: data.invitees_count,
  name: data.name,
  recordingStatus: data.recording_status,
  recordingUrl: data.recording_url ?? undefined,
  startTime: new Date(data.start_time),
  status: data.status,
});

export type ProgramPublicEvent = {
  administrators: Array<{ id: string }>;
  bmgId: null | string;
  coverPhoto?: Image;
  dialInInformation: ProgramDialInInformation;
  duration: number;
  endTime: Date;
  eventType: ProgramEventType;
  htmlContent: null | string;
  id: string;
  meta?: ProgramEventMeta;
  name: string;
  recordingStatus: EventRecordingStatus;
  recordingUrl?: string;
  startTime: Date;
  status: EventStatus | ProgramEventStatus;
  textContent: string;
  viewPermission: EventPrivacy;
};

export const createProgramPublicEvent = (data: ProgramPublicEventDataObject): ProgramPublicEvent => ({
  administrators: data.administrators,
  bmgId: data.bmg_id,
  coverPhoto: data.cover_photo ? createImage(data.cover_photo) : undefined,
  dialInInformation: data.dial_in_information,
  duration: data.duration,
  endTime: new Date(data.end_time),
  eventType: data.event_type,
  htmlContent: data.html_content,
  id: data.id,
  meta: data.meta ? createProgramEventMeta(data.meta) : undefined,
  name: data.name,
  recordingStatus: data.recording_status,
  recordingUrl: data.recording_url ?? undefined,
  startTime: new Date(data.start_time),
  status: data.status,
  textContent: data.text_content,
  viewPermission: data.view_permission,
});

export const isProgramEventAfterOpenToJoin = ({ endTime }: { endTime: Date }) =>
  isAfter(new Date(), addMinutes(endTime, ProgramAbstractEvent.leaveOffset));

export const isProgramEventBeforeOpenToJoin = ({ startTime }: { startTime: Date }, role: ProgramEventInviteeRole) =>
  isBefore(new Date(), addMinutes(startTime, ProgramAbstractEvent.getEnterOffset(role)));

export const hasProgramEventFinished = ({ endTime }: { endTime: Date }) => isAfter(new Date(), endTime);

export const isProgramEventOngoing = ({ endTime, startTime }: { endTime: Date; startTime: Date }) =>
  isWithinInterval(new Date(), { end: endTime, start: startTime });

export const getProgramEventLastChangeRequestStatus = (event: {
  changeRequests?: ChangeRequest[];
}): ChangeRequestStatus | undefined => getLastChangeRequestStatus(event.changeRequests);

export const generateProgramEventPublishAt = ({
  isDuplicating,
  isProgram,
  publishAt,
}: {
  isDuplicating?: boolean;
  isProgram?: boolean;
  publishAt?: Date;
}) => {
  if (isProgram && publishAt && !isDuplicating) {
    return new Date(publishAt);
  }

  return undefined;
};

export const generateProgramEventStartDate = ({
  isDuplicating,
  publishAt,
  startTime,
}: {
  isDuplicating?: boolean;
  publishAt?: Date;
  startTime?: Date;
}) => {
  if (startTime) {
    if (isDuplicating) {
      const tomorrow = addDays(new Date(), 1);
      const updatedStartTime = addDays(startTime, 1);
      let finalStartTime = max([tomorrow, updatedStartTime]);

      finalStartTime = setHours(finalStartTime, getHours(startTime));
      finalStartTime = setMinutes(finalStartTime, getMinutes(startTime));
      return startOfMinute(finalStartTime);
    }

    return new Date(startTime);
  }

  if (publishAt) {
    return addHours(publishAt, 24);
  }

  const start = addMinutes(new Date(), 5);
  const remainder = 5 - (getMinutes(start) % 5);
  return startOfMinute(addMinutes(start, remainder));
};

export const getProgramEventEndDate = ({
  duration,
  endTime,
  isDuplicating,
  startTime,
}: {
  duration?: number;
  endTime?: Date;
  isDuplicating?: boolean;
  startTime: Date;
}) => {
  if (endTime && !isDuplicating) {
    return new Date(endTime);
  }

  if (!duration) {
    return undefined;
  }

  return addMinutes(startTime, duration);
};

export const getProgramEventSlimData = (data: ProgramEventSlimDataObject): ProgramEventSlim => ({
  coverPhoto: data.cover_photo ? createImage(data.cover_photo) : undefined,
  endTime: new Date(data.end_time),
  eventType: data.event_type,
  goingInviteesCount: data.going_invitees_count,
  id: data.id,
  name: data.name,
  nodeId: data.node_id,
  place: data.place,
  startTime: new Date(data.start_time),
});

export const isProgramEventInProgress = ({
  endDifferenceInMinutes = 58,
  endTime,
  startDifferenceInMinutes = 0,
  startTime,
}: {
  endDifferenceInMinutes?: number;
  endTime: Date | string;
  startDifferenceInMinutes?: number;
  startTime: Date | string;
}) =>
  isWithinInterval(new Date(), {
    end: addMinutes(typeof endTime === 'string' ? parseISO(endTime) : endTime, endDifferenceInMinutes),
    start: addMinutes(typeof startTime === 'string' ? parseISO(startTime) : startTime, startDifferenceInMinutes),
  });

export const isProgramEventLive = ({ endTime, startTime }: { endTime: Date | string; startTime: Date | string }) =>
  isWithinInterval(new Date(), { end: new Date(startTime), start: new Date(endTime) });
